import { useCallback, useEffect, useRef } from 'react';

import { HYPATIA_URL } from '@frontend/envConfig';
import { useStreamExecute } from '@frontend/features/agents/hooks';
import { executeSpecializedAgent } from '@frontend/features/agents/services/specialized-agent';
import { useInstanceId } from '@frontend/hooks';

import { useStewardAgent } from './useStewardAgent';

// Coalesce a burst of manual changes before telling the Steward.
const FLUSH_DELAY_MS = 1500;

/**
 * Tells the Steward about the user's manual board edits so it can learn from
 * them. Changes are buffered and, after a quiet window, sent as a single
 * fire-and-forget `user-updated:` message (one Steward run per burst, not per
 * drag). Uses the same no-param two-step launch as the ask bar but with its own
 * ephemeral thread and no response handling. The live Steward owns the
 * `user-updated:` prompt rule.
 */
export function useStewardNotifier() {
  const { instanceId } = useInstanceId();
  const { agentUuid } = useStewardAgent();
  const buffer = useRef<string[]>([]);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Fire-and-forget: we don't read chunks or completion.
  const { execute } = useStreamExecute({
    onChunk: () => {},
    onComplete: () => {},
    onError: () => {},
  });

  const flush = useCallback(async () => {
    const items = buffer.current;
    buffer.current = [];
    if (items.length === 0 || !agentUuid || !instanceId) return;
    const text = `user-updated:\n${items.map(s => `- ${s}`).join('\n')}`;
    try {
      const execUrl = `${HYPATIA_URL}/api/v1/${instanceId}/agents/specialized/${agentUuid}/execute`;
      const { data, etag } = await executeSpecializedAgent(execUrl, {}, false)();
      if (!data.memory_id) return;
      execute({
        instanceId,
        agentId: agentUuid,
        parameters: {},
        messages: [{ text }],
        memoryId: data.memory_id,
        etag: etag ?? data.etag ?? undefined,
      });
    } catch {
      /* fire-and-forget — a failed notification must never disrupt the board */
    }
  }, [agentUuid, instanceId, execute]);

  const notify = useCallback(
    (description: string) => {
      buffer.current.push(description);
      clearTimeout(timer.current);
      timer.current = setTimeout(flush, FLUSH_DELAY_MS);
    },
    [flush]
  );

  useEffect(() => () => clearTimeout(timer.current), []);

  return { notify };
}
